import { createColumnHelper } from "@tanstack/react-table";

import CrudActions from "components/crud/CrudActions";

const columnHelper =
    createColumnHelper();


/*
|--------------------------------------------------------------------------
| DATE
|--------------------------------------------------------------------------
*/

const formatDate = (
    value
) => {

    if (!value) {
        return "-";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return date.toLocaleDateString(
        "en-IN",
        {
            day: "2-digit",
            month: "short",
            year: "numeric",
        }
    );
};


/*
|--------------------------------------------------------------------------
| RETURN STATUS
|--------------------------------------------------------------------------
*/

const getReturnStatusClass = (
    value
) => {

    switch (value) {

        case "issued":
            return `
                border-blue-200
                bg-blue-50
                text-blue-700
            `;

        case "partially_returned":
            return `
                border-yellow-200
                bg-yellow-50
                text-yellow-700
            `;

        case "returned":
            return `
                border-green-200
                bg-green-50
                text-green-700
            `;

        case "converted":
            return `
                border-purple-200
                bg-purple-50
                text-purple-700
            `;

        case "cancelled":
            return `
                border-red-200
                bg-red-50
                text-red-700
            `;

        default:
            return `
                border-gray-200
                bg-gray-50
                text-gray-700
            `;
    }
};


/*
|--------------------------------------------------------------------------
| CONVERSION STATUS
|--------------------------------------------------------------------------
*/

const getConversionStatusClass = (
    value
) => {

    switch (value) {

        case "pending":
            return `
                border-yellow-200
                bg-yellow-50
                text-yellow-700
            `;

        case "partial":
            return `
                border-blue-200
                bg-blue-50
                text-blue-700
            `;

        case "converted":
            return `
                border-green-200
                bg-green-50
                text-green-700
            `;

        case "not_converted":
            return `
                border-gray-200
                bg-gray-50
                text-gray-700
            `;

        default:
            return `
                border-gray-200
                bg-gray-50
                text-gray-700
            `;
    }
};


/*
|--------------------------------------------------------------------------
| COLUMNS
|--------------------------------------------------------------------------
*/

export const sampleTrackingColumns = ({
    onView,
    onEdit,
    onCancel,
} = {}) => {

    return [

        /*
        |--------------------------------------------------------------------------
        | ISSUE DATE
        |--------------------------------------------------------------------------
        */

        columnHelper.accessor(
            "issueDate",
            {
                id: "issueDate",

                header: "Issue Date",

                cell: (info) => (
                    <span className="whitespace-nowrap">
                        {formatDate(
                            info.getValue()
                        )}
                    </span>
                ),
            }
        ),


        /*
        |--------------------------------------------------------------------------
        | SALES PERSON
        |--------------------------------------------------------------------------
        */

        columnHelper.accessor(
            "employee.name",
            {
                id: "employee",

                header: "Sales Person",

                cell: (info) => {

                    const item =
                        info.row.original;

                    return (
                        <div>

                            <div
                                className="
                                    font-semibold
                                    text-navy-700
                                "
                            >
                                {
                                    info.getValue() ||
                                    "-"
                                }
                            </div>

                            {
                                item?.employee?.employeeCode && (

                                    <div
                                        className="
                                            text-xs
                                            text-gray-500
                                        "
                                    >
                                        {
                                            item.employee.employeeCode
                                        }
                                    </div>

                                )
                            }

                        </div>
                    );
                },
            }
        ),


        /*
        |--------------------------------------------------------------------------
        | SCHOOL
        |--------------------------------------------------------------------------
        */

        columnHelper.accessor(
            "school.name",
            {
                id: "school",

                header: "School",

                cell: (info) => {

                    const item =
                        info.row.original;

                    return (
                        <div>

                            <div
                                className="
                                    font-medium
                                    text-gray-800
                                "
                            >
                                {
                                    info.getValue() ||
                                    "-"
                                }
                            </div>

                            {
                                item?.school?.schoolCode && (

                                    <div
                                        className="
                                            text-xs
                                            text-gray-500
                                        "
                                    >
                                        {
                                            item.school.schoolCode
                                        }
                                    </div>

                                )
                            }

                        </div>
                    );
                },
            }
        ),


        /*
        |--------------------------------------------------------------------------
        | BOOK
        |--------------------------------------------------------------------------
        */

        columnHelper.accessor(
            "book.title",
            {
                id: "book",

                header: "Book",

                cell: (info) => {

                    const item =
                        info.row.original;

                    return (
                        <div>

                            <div
                                className="
                                    font-semibold
                                    text-gray-800
                                "
                            >
                                {
                                    info.getValue() ||
                                    "-"
                                }
                            </div>

                            {
                                item?.book?.isbn && (

                                    <div
                                        className="
                                            text-xs
                                            text-gray-500
                                        "
                                    >
                                        ISBN:{" "}
                                        {
                                            item.book.isbn
                                        }
                                    </div>

                                )
                            }

                        </div>
                    );
                },
            }
        ),


        /*
        |--------------------------------------------------------------------------
        | ISSUED QUANTITY
        |--------------------------------------------------------------------------
        */

        columnHelper.accessor(
            "quantity",
            {
                id: "quantity",

                header: "Issued Qty",

                cell: (info) => (
                    <span
                        className="
                            font-bold
                            text-gray-800
                        "
                    >
                        {
                            info.getValue() ||
                            0
                        }
                    </span>
                ),
            }
        ),


        /*
        |--------------------------------------------------------------------------
        | RETURNED
        |--------------------------------------------------------------------------
        */

        columnHelper.display({

            id: "returnedQuantity",

            header: "Returned",

            cell: ({ row }) => (

                <span
                    className="
                        font-semibold
                        text-green-600
                    "
                >
                    {
                        row.original?.summary
                            ?.returnedQuantity ||
                        0
                    }
                </span>

            ),
        }),


        /*
        |--------------------------------------------------------------------------
        | REMAINING
        |--------------------------------------------------------------------------
        */

        columnHelper.display({

            id: "remainingQuantity",

            header: "Remaining",

            cell: ({ row }) => {

                const quantity =
                    row.original?.summary
                        ?.remainingQuantity ||
                    0;

                return (
                    <span
                        className={
                            quantity > 0
                                ? `
                                    font-semibold
                                    text-orange-600
                                `
                                : `
                                    font-semibold
                                    text-green-600
                                `
                        }
                    >
                        {quantity}
                    </span>
                );
            },
        }),


        /*
        |--------------------------------------------------------------------------
        | RETURN STATUS
        |--------------------------------------------------------------------------
        */

        columnHelper.accessor(
            "returnStatus",
            {
                id: "returnStatus",

                header: "Return Status",

                cell: (info) => {

                    const value =
                        info.getValue();

                    return (
                        <span
                            className={`
                                inline-flex
                                rounded-full
                                border
                                px-3
                                py-1
                                text-xs
                                font-semibold
                                capitalize
                                ${getReturnStatusClass(
                                    value
                                )}
                            `}
                        >
                            {
                                String(
                                    value || "-"
                                ).replace(
                                    /_/g,
                                    " "
                                )
                            }
                        </span>
                    );
                },
            }
        ),


        /*
        |--------------------------------------------------------------------------
        | CONVERSION STATUS
        |--------------------------------------------------------------------------
        */

        columnHelper.accessor(
            "conversionStatus",
            {
                id: "conversionStatus",

                header: "Conversion",

                cell: (info) => {

                    const value =
                        info.getValue();

                    return (
                        <span
                            className={`
                                inline-flex
                                rounded-full
                                border
                                px-3
                                py-1
                                text-xs
                                font-semibold
                                capitalize
                                ${getConversionStatusClass(
                                    value
                                )}
                            `}
                        >
                            {
                                String(
                                    value || "-"
                                ).replace(
                                    /_/g,
                                    " "
                                )
                            }
                        </span>
                    );
                },
            }
        ),


        /*
        |--------------------------------------------------------------------------
        | ACTIONS
        |--------------------------------------------------------------------------
        */

        columnHelper.display({

            id: "actions",

            header: "Actions",

            cell: ({ row }) => {

                const item =
                    row.original;


                /*
                |--------------------------------------------------------------------------
                | EDIT
                |--------------------------------------------------------------------------
                |
                | Your existing logic allowed editing while the issue
                | is still active.
                |
                */

                const canEdit =
                    [
                        "issued",
                        "partially_returned",
                    ].includes(
                        item?.status
                    ) ||
                    [
                        "issued",
                        "partially_returned",
                    ].includes(
                        item?.returnStatus
                    );


                /*
                |--------------------------------------------------------------------------
                | CANCEL
                |--------------------------------------------------------------------------
                */

                const canCancel =
                    item?.returnStatus !==
                    "cancelled";


                return (

                    <CrudActions

                        /*
                        |--------------------------------------------------------------------------
                        | VIEW
                        |--------------------------------------------------------------------------
                        */

                        showView={
                            typeof onView ===
                            "function"
                        }

                        onView={
                            onView
                                ? () =>
                                    onView(item)
                                : undefined
                        }

                        viewTitle="View Sample Issue"


                        /*
                        |--------------------------------------------------------------------------
                        | EDIT
                        |--------------------------------------------------------------------------
                        */

                        showEdit={
                            canEdit &&
                            typeof onEdit ===
                                "function"
                        }

                        onEdit={
                            canEdit &&
                            typeof onEdit ===
                                "function"
                                ? () =>
                                    onEdit(item)
                                : undefined
                        }

                        editTitle="Edit Sample Issue"


                        /*
                        |--------------------------------------------------------------------------
                        | DELETE / CANCEL
                        |--------------------------------------------------------------------------
                        */

                        showDelete={
                            canCancel &&
                            typeof onCancel ===
                                "function"
                        }

                        onDelete={
                            canCancel &&
                            typeof onCancel ===
                                "function"
                                ? () =>
                                    onCancel(item)
                                : undefined
                        }

                        deleteTitle="Cancel Sample Issue"

                    />

                );
            },
        }),

    ];
};